/**
 * ALT pseudo-sport wire tokens — the PURE half (no registry, no store, no
 * env), safe to import from the SharedWorker.
 *
 * Spec: `galactus-dev-workspace/docs/agents/handoffs/main-alt-partition-he6-he7/`
 * (D1, D5). A deep sport's ALT lines (player props, game props / team stats,
 * team totals, period lines) are peeled off its mainline edge onto a second
 * engine under a derived sport key: the bare token stays MAIN (`CFB` on he2,
 * `NFL` on he5) and the ALT key is the bare token + `ALT` (`CFBALT` on he6,
 * `NFLALT` on he7). The ALT key rides every sport-keyed surface unchanged —
 * feedctl assignment, the directory row, `activeSports`, the `sports[]`
 * filter axis, `?sport=` on REST, and each row's `sport` field — so a
 * consumer needs no new grammar, only a token map.
 *
 * The suffix rule is deliberately the ONLY thing this module knows. The
 * registry-backed half (`altBaseSport`, `normalizeFeedSport`, …) lives in
 * `stores/marketFrameNormalization` next to `normalizeSport`, because it
 * needs the sport registry and the worker bundle must not pull that in.
 */

/** Wire suffix that marks an ALT pseudo-sport token (`CFBALT`, `NFLALT`). */
export const ALT_WIRE_SUFFIX = 'ALT';

/** True when `code` is shaped like an ALT pseudo-sport token: a non-empty
 *  base followed by the `ALT` suffix. Case-insensitive because the tab
 *  lowercases internally while the wire is uppercase. The base is NOT
 *  checked against the registry here (see the header); callers that need
 *  a real base sport use `altBaseSport`. */
export function isAltWireToken(code: string): boolean {
  if (typeof code !== 'string') return false;
  const c = code.trim();
  if (c.length <= ALT_WIRE_SUFFIX.length) return false;
  // Letters/digits only: "ditch the dash, just nflalt and cfbalt" (Dan) —
  // the token must survive every `[A-Z]+`-style sport regex unchanged.
  return c.slice(-ALT_WIRE_SUFFIX.length).toUpperCase() === ALT_WIRE_SUFFIX
    && /^[A-Za-z0-9]+$/.test(c);
}

/** The base wire code an ALT token derives from, case preserved
 *  (`CFBALT` → `CFB`, `cfbalt` → `cfb`); null for a non-ALT token. */
export function altBaseWireCode(code: string): string | null {
  if (!isAltWireToken(code)) return null;
  return code.trim().slice(0, -ALT_WIRE_SUFFIX.length);
}

/** The ALT token for a base wire code, same case (`CFB` → `CFBALT`,
 *  `cfb` → `cfbalt`). */
export function altWireTokenFor(baseCode: string): string {
  const base = baseCode.trim();
  const upper = base === base.toUpperCase();
  return base + (upper ? ALT_WIRE_SUFFIX : ALT_WIRE_SUFFIX.toLowerCase());
}

/** D5.4 — an ALT-only feed. A feed whose live announce (or, before it has
 *  announced, its claim scope) is made ENTIRELY of ALT tokens serves nothing
 *  a main page renders. Returns the uppercase token set when that holds,
 *  else null (a mainline or mixed feed, or one with no information yet —
 *  never muted on a guess).
 *
 *  `announced` wins when it carries anything: the engine's own announce is
 *  the ownership truth. An EMPTY announce (cold cache / serving nothing)
 *  falls back to the claim scope — a cold ALT edge is still an ALT edge. */
export function altOnlyFeedTokens(
  announced: readonly string[] | null,
  claim: ReadonlySet<string> | null,
): Set<string> | null {
  const src: readonly string[] = announced && announced.length > 0 ? announced : claim ? [...claim] : [];
  if (src.length === 0) return null;
  const tokens = new Set<string>();
  for (const raw of src) {
    if (typeof raw !== 'string') continue;
    const code = raw.trim().toUpperCase();
    if (!code) continue;
    if (!isAltWireToken(code)) return null;
    tokens.add(code);
  }
  return tokens.size > 0 ? tokens : null;
}
